'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { ChevronDown, ChevronUp, MoreHorizontal, X } from 'lucide-react'

import { createBrowserClient } from '@/supabase'
import { bookmarkProject, unbookmarkProject } from '@/apis/bookmark.service'
import { toast } from '@/hooks/use-toast'
import { ToastAction } from '@/components/ui/toast'

import type { FreelancerMarketProjectListRow } from '@/types/freelancer-market-project'
import { mapFreelancerMarketProjectRowToCard } from '@/lib/project/mappers'
import { ProjectCard } from '@/components/projects/ProjectCard'
import type { ProjectCardModel } from '@/types/project-card-model'
import { MAIN_CATEGORIES } from '@/constants/project-categories'
import { calculateDaysUntilDue } from '@/utils/dashboard-status'
import {
  FilterChipRow,
  FilterMoreButton,
  FilterPanel,
  FilterMainToggle,
  FilterMainPanel,
} from '@/components/filters/FilterBar'

type StatusFilter = 'all' | 'recruiting' | 'closed'
type SortBy = 'latest' | 'budget_high' | 'recommended' | 'deadline'
type ProjectTab = 'all' | 'recommended' | 'saved'
type MatchTier = '높음' | '중간' | '낮음'

type RecommendationPreferences = {
  roles: string[]
  skills: string[]
  experienceLevel: '초급' | '중급' | '전문가'
  workType: '상관없음' | '원격' | '하이브리드' | '상주'
  contractType: '상관없음' | '일회성' | '지속'
  weeklyHours: '상관없음' | '10-20h' | '20-40h'
  budgetPreference: '상관없음' | '저예산' | '중간' | '고예산'
}


type RecommendationChip = {
  key: string
  label: string
  onRemove: () => void
}

const DEFAULT_RECOMMENDATION_PREFERENCES: RecommendationPreferences = {
  roles: ['UX/UI'],
  skills: ['AWS', '보안'],
  experienceLevel: '중급',
  workType: '원격',
  contractType: '일회성',
  weeklyHours: '10-20h',
  budgetPreference: '중간',
}

const normalizeSearchQuery = (value: string) =>
  value.replace(/\s+/g, ' ').trim()

const LEGACY_CATEGORY_TO_FEILD: Record<string, string> = {
  'web-app': '웹 개발',
  'ai-data': '인공지능(AI)/데이터',
  devops: '인프라/클라우드/보안',
  planning: '기획/PM',
  ecommerce: '소프트웨어/솔루션',
  etl: '인공지능(AI)/데이터',
  cicd: '인프라/클라우드/보안',
}

const DISMISS_REASON_OPTIONS = [
  { label: '예산이 맞지 않음', value: '예산' },
  { label: '원격/상주 조건이 맞지 않음', value: '원격' },
  { label: '기간이 맞지 않음', value: '기간' },
  { label: '필요 스킬이 맞지 않음', value: '스킬' },
  { label: '경험 수준이 맞지 않음', value: '경험수준' },
  { label: '기타', value: '기타' },
] as const

const FreelancerMarketProjectsClient: React.FC = () => {
  const router = useRouter()
  const searchParams = useSearchParams()

  const [supabase] = useState(() =>
    typeof window !== 'undefined' ? createBrowserClient() : null,
  )

  const [projects, setProjects] = useState<ProjectCardModel[]>([])
  const [loading, setLoading] = useState(true)

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [feildFilter, setFeildFilter] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<SortBy>('latest')
  const [activeTab, setActiveTab] = useState<ProjectTab>('all')
  const [isRecommendationConfigOpen, setIsRecommendationConfigOpen] =
    useState(false)
  const [recommendationPreferences, setRecommendationPreferences] =
    useState<RecommendationPreferences>(DEFAULT_RECOMMENDATION_PREFERENCES)
  const [hiddenProjectIds, setHiddenProjectIds] = useState<Set<number>>(
    new Set(),
  )

  const [bookmarkedProjects, setBookmarkedProjects] = useState<Set<number>>(
    new Set(),
  )
  const bookmarkPendingRef = useRef<Set<number>>(new Set())
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [isFilterExpanded, setIsFilterExpanded] = useState(false)
  const [isFieldExpanded, setIsFieldExpanded] = useState(false)
  const [pendingDismissProject, setPendingDismissProject] = useState<ProjectCardModel | null>(null)
  const [expandedReasonProjectIds, setExpandedReasonProjectIds] = useState<Set<number>>(new Set())
  const [activeRecommendationMenuId, setActiveRecommendationMenuId] = useState<number | null>(null)

  const updateQueryParams = (updater: (params: URLSearchParams) => void) => {
    const current = searchParams.toString()
    const params = new URLSearchParams(current)
    updater(params)
    const next = params.toString()
    if (next === current) return
    router.push(next ? `?${next}` : '/freelancer/projects', { scroll: false })
  }

  useEffect(() => {
    const feildParam = searchParams.get('feild')
    const legacyCategory = searchParams.get('category')
    const normalizedFeild =
      feildParam ||
      (legacyCategory ? LEGACY_CATEGORY_TO_FEILD[legacyCategory] : null)
    setFeildFilter(normalizedFeild ?? null)

    const statusParam = searchParams.get('status')
    if (statusParam === 'recruiting' || statusParam === 'closed')
      setStatusFilter(statusParam)
    else setStatusFilter('all')

    const sortParam = searchParams.get('sort')
    if (sortParam === 'budget_high' || sortParam === 'recommended' || sortParam === 'deadline') setSortBy(sortParam)
    else setSortBy('latest')

    const qParam = normalizeSearchQuery(searchParams.get('q') ?? '')
    setSearchTerm(qParam)
  }, [searchParams])

  useEffect(() => {
    const currentQ = normalizeSearchQuery(searchParams.get('q') ?? '')
    const nextQ = normalizeSearchQuery(searchTerm)
    if (currentQ === nextQ) return

    const timer = setTimeout(() => {
      updateQueryParams((params) => {
        if (nextQ) params.set('q', nextQ)
        else params.delete('q')
      })
    }, 250)

    return () => clearTimeout(timer)
  }, [searchTerm, searchParams])

  useEffect(() => {
    if (!supabase) return

    const fetchData = async () => {
      try {
        setLoading(true)

        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser()
        if (userError) throw userError

        setCurrentUserId(user?.id ?? null)

        const { data, error } = await supabase.rpc(
          'rpc_freelancer_market_projects_rollup',
          {
            p_statuses: ['pending', 'matched', 'end'],
          },
        )
        if (error) throw error

        const rows = (data ?? []) as FreelancerMarketProjectListRow[]

        const mappedProjects: ProjectCardModel[] = rows.map((r) =>
          mapFreelancerMarketProjectRowToCard(r, user?.id ?? null),
        )
        setProjects(mappedProjects)

        const bookmarkedSet = new Set<number>()
        for (const r of rows) {
          if (r.is_bookmarked) bookmarkedSet.add(r.counsel_id)
        }
        setBookmarkedProjects(bookmarkedSet)
      } catch (err) {
        console.error('Error fetching market projects:', err)
        toast({
          variant: 'destructive',
          title: '프로젝트 조회 실패',
          description: '프로젝트 목록을 불러오는데 실패했습니다.',
        })
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [supabase])

  const handleFeildFilterChange = (feild: string | null) => {
    setFeildFilter(feild)
    updateQueryParams((params) => {
      params.delete('category')
      if (feild) params.set('feild', feild)
      else params.delete('feild')
    })
  }

  const handleStatusFilterChange = (status: StatusFilter) => {
    setStatusFilter(status)
    updateQueryParams((params) => {
      if (status === 'all') params.delete('status')
      else params.set('status', status)
    })
  }

  const handleSortByChange = (sort: SortBy) => {
    setSortBy(sort)
    updateQueryParams((params) => {
      if (sort === 'latest') params.delete('sort')
      else params.set('sort', sort)
    })
  }

  const extractBudget = (costStr: string): number => {
    const normalized = (costStr ?? '').replace(/\s+/g, ' ').trim()
    if (!normalized) return 0

    const parseBudgetChunk = (chunk: string): number => {
      const tokenRegex = /(\d[\d,]*(?:\.\d+)?)\s*(억|만|천)?/g
      let total = 0
      let matched = false
      let match: RegExpExecArray | null = null

      while ((match = tokenRegex.exec(chunk)) !== null) {
        matched = true
        const value = parseFloat((match[1] || '0').replace(/,/g, ''))
        const unit = match[2]

        if (Number.isNaN(value)) continue

        if (unit === '억') total += value * 100000000
        else if (unit === '만') total += value * 10000
        else if (unit === '천') total += value * 1000
        else total += value
      }

      return matched ? total : 0
    }

    const rangeParts = normalized
      .split(/~|∼|-/)
      .map((part) => part.trim())
      .filter(Boolean)

    if (rangeParts.length >= 2) return Math.max(...rangeParts.map(parseBudgetChunk))
    return parseBudgetChunk(normalized)
  }

  const statusCounts = useMemo(() => {
    const all = projects.length
    const recruiting = projects.filter((p) => p.counselStatus === 'pending' || p.counselStatus === 'matched').length
    const closed = projects.filter((p) => p.counselStatus === 'end').length

    return { all, recruiting, closed }
  }, [projects])

  const statusOptions = useMemo(
    () => [
      { value: 'all' as const, label: '전체', count: statusCounts.all },
      {
        value: 'recruiting' as const,
        label: '모집중',
        count: statusCounts.recruiting,
      },
      { value: 'closed' as const, label: '마감됨', count: statusCounts.closed },
    ],
    [statusCounts],
  )

  const filteredProjects = useMemo(() => {
    let filtered = projects

    if (feildFilter) {
      filtered = filtered.filter(
        (project) => project.feilds?.includes(feildFilter) ?? false,
      )
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter((project) => {
        return (
          project.title?.toLowerCase().includes(searchLower) ||
          project.feilds?.some((f) => f.toLowerCase().includes(searchLower)) ||
          project.skills?.some((skill) =>
            skill.toLowerCase().includes(searchLower),
          )
        )
      })
    }

    if (statusFilter === 'recruiting') {
      filtered = filtered.filter((project) => project.counselStatus === 'pending' || project.counselStatus === 'matched')
    } else if (statusFilter === 'closed') {
      filtered = filtered.filter((project) => project.counselStatus === 'end')
    }

    const computeRecommendationScore = (project: ProjectCardModel) => {
      const title = project.title?.toLowerCase() ?? ''
      const skills = project.skills?.map((skill) => skill.toLowerCase()) ?? []
      const hasReact = title.includes('react') || skills.some((skill) => skill.includes('react'))
      const hasRecentDate = Date.now() - new Date(project.createdAt).getTime() <= 1000 * 60 * 60 * 24 * 14
      let score = 0
      if (hasReact) score += 3
      if (project.workType === 'REMOTE' || project.workType === 'HYBRID') score += 2
      if (hasRecentDate) score += 1
      if (extractBudget(project.cost) > 0) score += 1
      if (project.period) score += 1
      return score
    }

    return [...filtered].sort((a, b) => {
      if (sortBy === 'recommended') return computeRecommendationScore(b) - computeRecommendationScore(a)
      if (sortBy === 'latest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      if (sortBy === 'deadline') {
        const aDue = calculateDaysUntilDue(a.startDate) ?? Number.MAX_SAFE_INTEGER
        const bDue = calculateDaysUntilDue(b.startDate) ?? Number.MAX_SAFE_INTEGER
        return aDue - bDue
      }
      if (sortBy === 'budget_high') return extractBudget(b.cost) - extractBudget(a.cost)
      return 0
    })
  }, [projects, feildFilter, searchTerm, statusFilter, sortBy])


  const recommendationPreferenceChips = useMemo<RecommendationChip[]>(() => {
    const chips: RecommendationChip[] = []

    recommendationPreferences.roles.slice(0, 2).forEach((role) => {
      chips.push({
        key: `role-${role}`,
        label: role,
        onRemove: () =>
          setRecommendationPreferences((prev) => ({
            ...prev,
            roles: prev.roles.filter((item) => item !== role),
          })),
      })
    })

    recommendationPreferences.skills.slice(0, 2).forEach((skill) => {
      chips.push({
        key: `skill-${skill}`,
        label: skill,
        onRemove: () =>
          setRecommendationPreferences((prev) => ({
            ...prev,
            skills: prev.skills.filter((item) => item !== skill),
          })),
      })
    })

    chips.push({
      key: 'experienceLevel',
      label: `${recommendationPreferences.experienceLevel} 이상`,
      onRemove: () =>
        setRecommendationPreferences((prev) => ({
          ...prev,
          experienceLevel: '초급',
        })),
    })

    if (recommendationPreferences.workType !== '상관없음') {
      chips.push({
        key: 'workType',
        label: recommendationPreferences.workType,
        onRemove: () =>
          setRecommendationPreferences((prev) => ({
            ...prev,
            workType: '상관없음',
          })),
      })
    }

    if (recommendationPreferences.weeklyHours !== '상관없음') {
      chips.push({
        key: 'weeklyHours',
        label: `${recommendationPreferences.weeklyHours}/주`,
        onRemove: () =>
          setRecommendationPreferences((prev) => ({
            ...prev,
            weeklyHours: '상관없음',
          })),
      })
    }

    return chips
  }, [recommendationPreferences])

  const getBudgetBand = (
    amount: number,
  ): RecommendationPreferences['budgetPreference'] => {
    if (amount < 5000000) return '저예산'
    if (amount < 15000000) return '중간'
    return '고예산'
  }

  const getMatchTier = (score: number): MatchTier => {
    if (score >= 70) return '높음'
    if (score >= 45) return '중간'
    return '낮음'
  }

  const recommendedProjects = useMemo(() => {
    return filteredProjects
      .filter((project) => !hiddenProjectIds.has(project.id))
      .map((project) => {
        const reasons: string[] = []
        let score = 0

        const projectSkills = project.skills || []
        const matchingSkills = projectSkills.filter((skill) =>
          recommendationPreferences.skills.some((preferredSkill) =>
            skill.toLowerCase().includes(preferredSkill.toLowerCase()),
          ),
        )
        if (matchingSkills.length > 0) {
          score += 35
          reasons.push(`내 스킬: ${matchingSkills.slice(0, 2).join('·')} 일치`)
        }

        if (recommendationPreferences.experienceLevel !== '초급') {
          score += 20
          reasons.push(
            `경험 수준: ${recommendationPreferences.experienceLevel} 선호와 유사`,
          )
        }

        const workTypeLabel =
          project.workType === 'REMOTE'
            ? '원격'
            : project.workType === 'HYBRID'
              ? '하이브리드'
              : '상주'
        if (
          recommendationPreferences.workType === '상관없음' ||
          recommendationPreferences.workType === workTypeLabel
        ) {
          score += 15
          reasons.push(`${workTypeLabel} 근무 형태 선호와 일치`)
        }

        if (recommendationPreferences.contractType !== '상관없음') {
          score += 10
          reasons.push(
            `프로젝트 유형: ${recommendationPreferences.contractType} 선호 반영`,
          )
        }

        if (recommendationPreferences.weeklyHours !== '상관없음') {
          score += 10
          reasons.push(
            `주당 가능 시간(${recommendationPreferences.weeklyHours})과 유사`,
          )
        }

        const budgetValue = extractBudget(project.cost)
        if (
          recommendationPreferences.budgetPreference === '상관없음' ||
          recommendationPreferences.budgetPreference ===
            getBudgetBand(budgetValue)
        ) {
          score += 10
          reasons.push('예산 범위 선호와 유사')
        }

        const capped = Math.min(100, score)
        return {
          project,
          matchScore: capped,
          matchTier: getMatchTier(capped),
          reasons: reasons.slice(0, 3),
        }
      })
      .sort((a, b) => b.matchScore - a.matchScore)
  }, [filteredProjects, hiddenProjectIds, recommendationPreferences])

  const visibleProjects = useMemo(() => {
    if (activeTab === 'saved') {
      return filteredProjects.filter((project) =>
        bookmarkedProjects.has(project.id),
      )
    }

    if (activeTab === 'recommended') {
      return recommendedProjects.map((item) => item.project)
    }

    return filteredProjects
  }, [activeTab, bookmarkedProjects, filteredProjects, recommendedProjects])

  const recommendationMetaById = useMemo(() => {
    return new Map(recommendedProjects.map((item) => [item.project.id, item]))
  }, [recommendedProjects])

  const handleProjectClick = (id: number) => {
    router.push(`/freelancer/projects/${id}`)
  }

  const handleDismissRecommendation = (reason: string) => {
    if (!pendingDismissProject) return
    const projectId = pendingDismissProject.id
    setHiddenProjectIds((prev) => new Set(prev).add(projectId))
    setActiveRecommendationMenuId(null)
    const dismissedProject = pendingDismissProject
    toast({
      title: '추천에서 숨김 처리됨',
      description: `선택한 이유: ${reason}. 피드백이 반영됩니다.`,
      action: (
        <ToastAction
          altText="추천 다시 표시"
          onClick={() => {
            setHiddenProjectIds((prev) => {
              const next = new Set(prev)
              next.delete(projectId)
              return next
            })
            toast({
              title: '되돌리기 완료',
              description: `${dismissedProject.title} 프로젝트를 다시 표시합니다.`,
            })
          }}
        >
          실행 취소
        </ToastAction>
      ),
    })
    setPendingDismissProject(null)
  }

  return (
    <div className="w-full space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          프로젝트 찾기
        </h1>
        <p className="text-sm text-gray-600">
          프로젝트를 탐색하고 견적을 제출하세요
        </p>

        <div className="flex gap-2 pt-2">
          {[
            { value: 'all' as const, label: '전체' },
            { value: 'recommended' as const, label: '추천' },
            { value: 'saved' as const, label: '저장' },
          ].map((tab) => {
            const isActive = activeTab === tab.value
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      </header>

      {activeTab === 'recommended' && (
        <section className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-blue-900">
                내 추천 기준
              </p>
              <p className="text-xs text-blue-800">추천 기준 {recommendationPreferenceChips.length}개 적용됨</p>
            </div>
            <button
              type="button"
              onClick={() => setIsRecommendationConfigOpen((prev) => !prev)}
              className="text-sm font-medium text-blue-700 hover:text-blue-900"
            >
              {isRecommendationConfigOpen ? '기준 접기' : '기준 수정'}
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {recommendationPreferenceChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.onRemove}
                className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-white px-2.5 py-1 text-xs text-blue-800 hover:bg-blue-100"
                title="추천 기준에서 제외"
              >
                <span>{chip.label}</span>
                <X className="h-3 w-3" />
              </button>
            ))}
          </div>

          {isRecommendationConfigOpen && (
            <div className="grid sm:grid-cols-2 gap-3 border-t border-blue-100 pt-3">
              <label className="text-sm text-gray-700">
                경험 수준
                <select
                  value={recommendationPreferences.experienceLevel}
                  onChange={(e) =>
                    setRecommendationPreferences((prev) => ({
                      ...prev,
                      experienceLevel: e.target
                        .value as RecommendationPreferences['experienceLevel'],
                    }))
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
                >
                  <option value="초급">초급</option>
                  <option value="중급">중급</option>
                  <option value="전문가">전문가</option>
                </select>
              </label>

              <label className="text-sm text-gray-700">
                근무 형태
                <select
                  value={recommendationPreferences.workType}
                  onChange={(e) =>
                    setRecommendationPreferences((prev) => ({
                      ...prev,
                      workType: e.target
                        .value as RecommendationPreferences['workType'],
                    }))
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
                >
                  <option value="상관없음">상관없음</option>
                  <option value="원격">원격</option>
                  <option value="하이브리드">하이브리드</option>
                  <option value="상주">상주</option>
                </select>
              </label>
            </div>
          )}
        </section>
      )}

      {/* 검색 및 핵심 필터 */}
      <section className="space-y-3">
        <input
          type="text"
          placeholder="프로젝트 제목, 분야, 기술 스택으로 검색..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all bg-white"
        />

        <div className="flex flex-wrap items-center gap-2">
          <FilterMainToggle
            label="분야"
            selectedLabel={
              feildFilter
                ? MAIN_CATEGORIES.find((c) => c.value === feildFilter)
                    ?.label || feildFilter
                : undefined
            }
            expanded={isFieldExpanded}
            onToggle={() => setIsFieldExpanded((v) => !v)}
            selected={!!feildFilter}
          />
          <select
            value={sortBy}
            onChange={(e) => handleSortByChange(e.target.value as SortBy)}
            className="h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-700"
          >
            <option value="recommended">추천순</option>
            <option value="latest">최신순</option>
            <option value="budget_high">예산 높은순</option>
            <option value="deadline">마감임박</option>
          </select>
          <FilterMoreButton
            expanded={isFilterExpanded}
            onToggle={() => setIsFilterExpanded((v) => !v)}
            label="필터 더보기"
            compact
          />
        </div>

        {isFieldExpanded && (
          <FilterMainPanel>
            <button
              type="button"
              onClick={() => {
                handleFeildFilterChange(null)
                setIsFieldExpanded(false)
              }}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                !feildFilter ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              전체
            </button>
            {MAIN_CATEGORIES.map((category) => {
              const active = feildFilter === category.value
              return (
                <button
                  key={category.value}
                  type="button"
                  onClick={() => {
                    handleFeildFilterChange(category.value)
                    setIsFieldExpanded(false)
                  }}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    active ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {category.label}
                </button>
              )
            })}
          </FilterMainPanel>
        )}

        <FilterChipRow
          chips={[
            ...(feildFilter
              ? [
                  {
                    key: 'feild',
                    label:
                      MAIN_CATEGORIES.find((c) => c.value === feildFilter)
                        ?.label || feildFilter,
                    tone: 'blue' as const,
                    onClear: () => handleFeildFilterChange(null),
                  },
                ]
              : []),
            ...(statusFilter !== 'all'
              ? [
                  {
                    key: 'status',
                    label:
                      statusOptions.find((o) => o.value === statusFilter)
                        ?.label || '',
                    tone: 'gray' as const,
                    onClear: () => handleStatusFilterChange('all'),
                  },
                ]
              : []),
            ...(sortBy !== 'latest'
              ? [
                  {
                    key: 'sort',
                    label:
                      sortBy === 'budget_high' ? '예산 높은순' : sortBy === 'deadline' ? '마감임박' : '추천순',
                    tone: 'gray' as const,
                    onClear: () => handleSortByChange('latest'),
                  },
                ]
              : []),
          ]}
        />
      </section>

      {isFilterExpanded && (
        <FilterPanel>
          {/* 상태 */}
          <div className="space-y-3">
            <h3 className="text-base font-semibold text-gray-900">
              프로젝트 상태
            </h3>

            <div className="flex flex-wrap gap-3">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleStatusFilterChange(option.value)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    statusFilter === option.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {option.label} ({option.count})
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4 space-y-3">
            <h3 className="text-base font-semibold text-gray-900">정렬</h3>
            <div className="flex flex-wrap gap-3">
              {[
                { value: 'recommended' as const, label: '추천순' },
                { value: 'latest' as const, label: '최신순' },
                { value: 'deadline' as const, label: '마감임박' },
                { value: 'budget_high' as const, label: '예산 높은순' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSortByChange(option.value)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    sortBy === option.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </FilterPanel>
      )}

      {/* 결과 통계 */}
      {!loading && (
        <div className="text-sm text-gray-600">
          총{' '}
          <span className="text-gray-900 font-semibold">
            {visibleProjects.length}
          </span>
          개의 프로젝트를 찾았습니다
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20 bg-white border border-gray-200 rounded-xl">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
            <p className="text-sm text-gray-600">프로젝트를 불러오는 중...</p>
          </div>
        </div>
      )}

      {!loading && (
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {visibleProjects.length === 0 ? (
            <div className="text-center py-20 bg-white border border-gray-200 rounded-xl">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  검색 결과가 없습니다
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {searchTerm
                    ? '다른 검색어로 시도해보세요'
                    : '필터 조건을 변경해보세요'}
                </p>

                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    검색어 초기화
                  </button>
                )}
              </div>
            </div>
          ) : (
            visibleProjects.map((project) => {
              const recommendationMeta = recommendationMetaById.get(project.id)

              return (
                <div key={project.id} className="space-y-2">
                  {activeTab === 'recommended' && recommendationMeta && (
                    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-gray-700">
                            맞춤도 {recommendationMeta.matchScore}% · {recommendationMeta.matchTier}
                          </div>
                          <div className="mt-1 h-1.5 w-full rounded-full bg-gray-100">
                            <div
                              className="h-full rounded-full bg-blue-500"
                              style={{ width: `${recommendationMeta.matchScore}%` }}
                            />
                          </div>
                        </div>
                        <div className="relative">
                          <button
                            type="button"
                            className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
                            onClick={() => setActiveRecommendationMenuId((prev) => (prev === project.id ? null : project.id))}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                          {activeRecommendationMenuId === project.id && (
                            <div className="absolute right-0 top-8 z-10 w-44 rounded-lg border border-gray-200 bg-white shadow-sm">
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                                onClick={() => {
                                  setPendingDismissProject(project)
                                  setActiveRecommendationMenuId(null)
                                }}
                              >
                                관심 없음
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="mt-2 inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                        onClick={() =>
                          setExpandedReasonProjectIds((prev) => {
                            const next = new Set(prev)
                            if (next.has(project.id)) next.delete(project.id)
                            else next.add(project.id)
                            return next
                          })
                        }
                      >
                        이유 보기
                        {expandedReasonProjectIds.has(project.id) ? (
                          <ChevronUp className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                      </button>
                      {expandedReasonProjectIds.has(project.id) && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {recommendationMeta.reasons.map((reason) => (
                            <span
                              key={reason}
                              className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-[11px] text-gray-700"
                            >
                              {reason}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {recommendationMeta.reasons.slice(0, 2).map((reason) => (
                          <span
                            key={`summary-${reason}`}
                            className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-[11px] text-blue-700"
                          >
                            {reason}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <ProjectCard
                    project={project}
                    mode="freelancerMarket"
                    onBookmarkToggle={async () => {
                      if (bookmarkPendingRef.current.has(project.id)) return

                      bookmarkPendingRef.current.add(project.id)

                      try {
                        if (bookmarkedProjects.has(project.id)) {
                          await unbookmarkProject(project.id)

                          setBookmarkedProjects((prev) => {
                            const next = new Set(prev)
                            next.delete(project.id)
                            return next
                          })

                          setProjects((prev) =>
                            prev.map((p) =>
                              p.id === project.id
                                ? { ...p, isBookmarked: false }
                                : p,
                            ),
                          )

                          toast({
                            title: '북마크 해제',
                            description: '관심 프로젝트에서 제거되었습니다.',
                          })
                        } else {
                          await bookmarkProject(project.id)

                          setBookmarkedProjects((prev) =>
                            new Set(prev).add(project.id),
                          )

                          setProjects((prev) =>
                            prev.map((p) =>
                              p.id === project.id
                                ? { ...p, isBookmarked: true }
                                : p,
                            ),
                          )

                          toast({
                            title: '북마크 추가',
                            description: '관심 프로젝트에 추가되었습니다.',
                          })
                        }
                      } catch (error: any) {
                        toast({
                          variant: 'destructive',
                          title: '북마크 실패',
                          description:
                            error?.message || '북마크 처리에 실패했습니다.',
                        })
                      } finally {
                        bookmarkPendingRef.current.delete(project.id)
                      }
                    }}
                    onClick={() => handleProjectClick(project.id)}
                    currentUserId={currentUserId}
                  />
                </div>
              )
            })
          )}
        </section>
      )}

      {pendingDismissProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white border border-gray-200 shadow-xl p-5 space-y-4">
            <h3 className="text-base font-semibold text-gray-900">관심 없음 이유를 선택해 주세요</h3>
            <p className="text-sm text-gray-600">선택한 이유는 추천 품질 개선에 사용됩니다.</p>
            <div className="space-y-2">
              {DISMISS_REASON_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleDismissRecommendation(option.value)}
                  className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setPendingDismissProject(null)}
              className="w-full px-3 py-2 rounded-lg text-sm border border-gray-200 hover:bg-gray-50"
            >
              닫기
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default FreelancerMarketProjectsClient
